import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import TickerTimeout from "../../../utils/TickerTimeout";
import anime from "animejs/lib/anime.es.js";
import {
  setShowPotCollectionOutro,
  setPotCollectionOutroTransitionActive,
  setPotCollectionOutroCounterFinished,
} from "../../../State/actions";
import { Settings } from "../../../settings";
import { PixiSettings } from "../../../pixiSettings";
import { FONT_STYLES, scaleStyle } from "../../../fontStyles";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import { HowlCache } from "../../../State/Slices/soundsSlice";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import { formatToCustomLocale } from "../../../utilities";
import FXManager from "../../../Engine/Effects/FXManager";

// Auto-close delay (GDD §19: 30 seconds)
const AUTO_CLOSE_DELAY =
  FEATURES_CONFIG.POT_COLLECTION.TOTAL_WIN_AUTO_CLOSE || 30000;

// Counter animation duration (ms)
const COUNTER_DURATION = 3000;

// Device and orientation-specific config
// scale: spine scale multiplier (on top of symbolHeight/SYMBOL_HEIGHT ratio)
// offset: spine Y offset multiplier (fraction of symbolHeight)
// buttonYOffset: TAP TO CONTINUE Y offset (fraction of symbolHeight, positive = down)
const DEVICE_MULTIPLIERS = {
  MOBILE_SMALL_PORTRAIT: { scale: 1.0, offset: 0, buttonYOffset: 1.25 },
  MOBILE_PORTRAIT: { scale: 1.0, offset: 0, buttonYOffset: 2.75 },
  MOBILE_LANDSCAPE: { scale: 0.8, offset: 0, buttonYOffset: 1.0 },
  TABLET_PORTRAIT: { scale: 1.4, offset: 0, buttonYOffset: 0 },
  TABLET_LANDSCAPE: { scale: 1.3, offset: 0, buttonYOffset: 0 },
  DESKTOP: { scale: 1.0, offset: 0, buttonYOffset: 0 },
};

/**
 * PotCollectionOutroDialogController
 *
 * Manages the Pot Collection total win outro popup.
 * Uses the win_celebration spine with the "total_win" skin.
 * Text is injected via spine slots (same pattern as WinAnimationSecondaryController):
 *   - txt_slot_value            → static win amount (no counter animation — GDD §19)
 *   - txt_slot_multiplier       → "X57 X BET" multiplier (revealed after value)
 *   - txt_slot_tap_to_continue  → "TAP TO CONTINUE" button
 *
 * Flow: setSkin("total_win") → in → idle → (show value + multiplier) →
 *       user click OR 30s auto-close → out → dispatch cleanup
 */
class PotCollectionOutroDialogController {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    t,
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.t = t;

    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.outroSpine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.isWaitingForClick = false;

    // User interaction state
    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;
    this.hasHandledCloseClick = false;
    this.debounceTimeout = null;
    this.skipProtectionTimeout = null;
    this.currentTimeout = null;
    this.autoCloseTimeout = null;
    this.currentTimeouts = [];

    // Win data
    this._totalWinValue = 0;
    this._betAmount = 0;

    // Counter (attached to spine slot — per-char cells matching WinAnimationSecondaryController)
    this.counterContainer = null;
    this._charTexts = [];
    this._counterBaseStyle = null;
    this.currentCounterValue = 0;
    this.counterAnimationRef = null;

    // Multiplier (attached to spine slot)
    this.multiplierContainer = null;
    this.multiplierText = null;

    // Button (attached to spine slot)
    this.buttonContainer = null;
    this.buttonText = null;

    // Click handler (set by component via setButtonClickHandler)
    this._buttonClickHandler = null;

    // Internal caches
    this._deviceMultipliersCache = null;
    this._availableAnimationsCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initializeOutroSpine();
  }

  // ===================================================================
  //  CONFIG / SCALING (matches WinAnimationSecondaryController)
  // ===================================================================

  setButtonClickHandler(handler) {
    this._buttonClickHandler = handler;
  }

  getDeviceMultipliers() {
    const { device, orientation, isMobileSmall } = this.dimensionsContext;

    if (
      this._deviceMultipliersCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation &&
      this._lastIsMobileSmall === isMobileSmall
    ) {
      return this._deviceMultipliersCache;
    }

    let multipliers;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multipliers = isMobileSmall
          ? DEVICE_MULTIPLIERS.MOBILE_SMALL_PORTRAIT
          : DEVICE_MULTIPLIERS.MOBILE_PORTRAIT;
      } else {
        multipliers = DEVICE_MULTIPLIERS.MOBILE_LANDSCAPE;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multipliers = DEVICE_MULTIPLIERS.TABLET_PORTRAIT;
      } else {
        multipliers = DEVICE_MULTIPLIERS.TABLET_LANDSCAPE;
      }
    } else {
      multipliers = DEVICE_MULTIPLIERS.DESKTOP;
    }

    this._deviceMultipliersCache = multipliers;
    this._lastDevice = device;
    this._lastOrientation = orientation;
    this._lastIsMobileSmall = isMobileSmall;
    return multipliers;
  }

  applyScaleAndCenter() {
    if (!this.outroSpine || !this.dimensions) return;

    // Proportional scaling based on symbol size (same as WinAnimationSecondaryController)
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    const { scale: deviceScaleMultiplier, offset: deviceOffsetMultiplier } =
      this.getDeviceMultipliers();
    scale *= deviceScaleMultiplier;

    this.outroSpine.scale.set(scale);

    const baseOffset = this.symbolHeight || 0;
    this.outroSpine.x = 0;
    this.outroSpine.y = -baseOffset * deviceOffsetMultiplier;

    // Keep coin shower proportional to spine on resize
    if (this._coinShowerContainer && !this._coinShowerContainer.destroyed) {
      this._coinShowerContainer.scale.set(scale * 2.5);
    }
  }

  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const oldDevice = this.dimensionsContext?.device;
    const newDevice = dimensionsContext?.device;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;

    if (!dimensionsChanged && !symbolHeightChanged) return;

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;
    this.symbolHeight = symbolHeight;

    if (
      oldDevice !== newDevice ||
      this._lastDevice !== dimensionsContext.device ||
      this._lastOrientation !== dimensionsContext.orientation ||
      this._lastIsMobileSmall !== dimensionsContext.isMobileSmall
    ) {
      this._deviceMultipliersCache = null;
      this._lastDevice = dimensionsContext.device;
      this._lastOrientation = dimensionsContext.orientation;
    }

    if (
      this.outroSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightChanged)
    ) {
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
        this.applyButtonOffset();
      }
    }
  }

  // ===================================================================
  //  SPINE CREATION
  // ===================================================================

  initializeOutroSpine() {
    try {
      this.outroSpine = this.createOutroSpine();

      if (!this.outroSpine) return;

      if (!this.outroSpine.skeleton || !this.outroSpine.state) return;
      if (!this.outroSpine.skeleton.data) return;

      this.container.addChild(this.outroSpine);
      this.outroSpine.alpha = 0.001;
      this.outroSpine.zIndex = 20;
      this.outroSpine.eventMode = "passive";

      this.applyScaleAndCenter();

      if (this.outroSpine.parent !== this.container) return;

      this.initialized = true;
      this._availableAnimationsCache = null;

      // Configure animation mixing for smooth transitions
      const stateData = this.outroSpine.state.data;
      stateData.setMix("in", "idle", 0.15);
      stateData.setMix("idle", "out", 0.25);

      this.warmUpOutroSpine();
      this.initializeCounter();
      this.initializeMultiplier();
      this.initializeButton();

      // Force-upload atlas textures to GPU
      SpineAtlasUploader.upload("win_celebration");
    } catch {
      // Silent fail
    }
  }

  createOutroSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "win_celebration-skeleton";
      const atlasAlias = "win_celebration-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

      const outroSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!outroSpine?.skeleton?.data?.animations) return null;

      return outroSpine;
    } catch {
      return null;
    }
  }

  warmUpOutroSpine() {
    if (!this.outroSpine?.state) return;
    try {
      // Apply total_win skin before warming up so bone structure is populated
      this.applySkin();

      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.outroSpine.state.setAnimation(0, animations[0], false);
        this.outroSpine.state.update(0.016);
        this.outroSpine.skeleton.updateWorldTransform();
      }
      this.outroSpine.alpha = 0.001;
    } catch {
      // Silent fail
    }
  }

  getAvailableAnimations() {
    if (!this.outroSpine?.skeleton?.data?.animations) return [];
    if (this._availableAnimationsCache !== null)
      return this._availableAnimationsCache;

    const animations = this.outroSpine.skeleton.data.animations.map(
      (a) => a.name,
    );
    this._availableAnimationsCache = animations;
    return animations;
  }

  /**
   * Apply the "total_win" skin to show Zeus with orbs artwork
   */
  applySkin() {
    if (!this.outroSpine?.skeleton) return;

    try {
      const skinData = this.outroSpine.skeleton.data.findSkin("total_win");
      if (skinData) {
        this.outroSpine.skeleton.setSkin(skinData);
        this.outroSpine.skeleton.setSlotsToSetupPose();
      }
    } catch {
      // Silent fail — skin may not exist
    }
  }

  // ===================================================================
  //  COUNTER (per-char cells — matches WinAnimationSecondaryController)
  // ===================================================================

  initializeCounter() {
    if (!this.outroSpine || !this.outroSpine.skeleton) return;

    const counterSlot = this.outroSpine.skeleton.findSlot("txt_slot_value");
    if (!counterSlot) return;

    this.counterContainer = new PIXI.Container();
    this.counterContainer.name = "pc_outro_counter_attached_to_slot";
    this.counterContainer.alpha = 0.001;

    // Use same font style as WinAnimationSecondaryController
    const counterBaseStyle = FONT_STYLES.winCelebrationCounter;
    this._counterBaseStyle = counterBaseStyle;
    this._counterStyle = scaleStyle(counterBaseStyle);

    // Measure per-character advance width accurately (same as WinAnimationSecondaryController):
    // "00".width / 2 gives the true advance width (shared padding cancels out)
    const measureStyle = new PIXI.TextStyle({
      fontFamily: this._counterStyle.fontFamily,
      fontSize: this._counterStyle.fontSize,
      fontWeight: this._counterStyle.fontWeight || "normal",
    });
    const CHAR_PADDING = 4;
    const doubleProbe = new PIXI.Text("00", measureStyle);
    this._digitCellWidth = (doubleProbe.width / 2) + CHAR_PADDING;
    doubleProbe.destroy();
    const sepProbe = new PIXI.Text(",,", measureStyle);
    this._separatorCellWidth = (sepProbe.width / 2) + CHAR_PADDING;
    sepProbe.destroy();

    // Per-character text objects will be created dynamically
    this._charTexts = [];

    // Attach to slot — Spine auto-updates position/rotation/scale
    this.outroSpine.addSlotObject("txt_slot_value", this.counterContainer);

    // Cache bone width for overflow scaling
    const bone = counterSlot.bone;
    this._counterBoneWidth = bone ? bone.data.length || 400 : 400;

    // Initialize with zero
    this.updateCounterDisplay(0);
  }

  /**
   * Max text width in spine-local coords for font-size fitting.
   * Uses screen width only — bone width is too narrow and over-shrinks text.
   */
  _getScreenLocalMaxWidth(screenRatio = 0.92) {
    const spineScale = Math.abs(this.outroSpine?.scale?.x || 1);
    return spineScale > 0
      ? ((this.dimensions?.width || 800) * screenRatio) / spineScale
      : (this.dimensions?.width || 800) * screenRatio;
  }

  /**
   * Measure per-char counter width at a given font size.
   */
  _measureCounterLayout(chars, fontSize) {
    const measureStyle = new PIXI.TextStyle({
      fontFamily: this._counterBaseStyle.fontFamily,
      fontSize,
      fontWeight: this._counterBaseStyle.fontWeight || "normal",
    });
    const CHAR_PADDING = 4;
    const doubleProbe = new PIXI.Text("00", measureStyle);
    const digitCellWidth = doubleProbe.width / 2 + CHAR_PADDING;
    doubleProbe.destroy();
    const sepProbe = new PIXI.Text(",,", measureStyle);
    const separatorCellWidth = sepProbe.width / 2 + CHAR_PADDING;
    sepProbe.destroy();

    let totalWidth = 0;
    const cellWidths = chars.map((ch) => {
      const isDigit = ch >= "0" && ch <= "9";
      const w = isDigit ? digitCellWidth : separatorCellWidth;
      totalWidth += w;
      return w;
    });

    return { totalWidth, cellWidths, fontSize };
  }

  /**
   * Update counter display with new value.
   * Uses per-character fixed-width cells so digits don't cause horizontal jitter.
   * Each character gets its own PIXI.Text centered in a cell of equal width.
   */
  updateCounterDisplay(value) {
    if (!this.counterContainer) return;

    const formattedText = formatToCustomLocale(Math.floor(value));
    const chars = formattedText.split("");

    const maxWidth = this._getScreenLocalMaxWidth(0.85);
    let fontSize = this._counterBaseStyle.fontSize;
    let layout = this._measureCounterLayout(chars, fontSize);

    // Shrink font size so text fits (same technique as reload dialog)
    if (layout.totalWidth > maxWidth && maxWidth > 0) {
      fontSize = Math.max(
        10,
        Math.floor(fontSize * (maxWidth / layout.totalWidth)),
      );
      layout = this._measureCounterLayout(chars, fontSize);
    }

    const charStyle = new PIXI.TextStyle(scaleStyle(this._counterBaseStyle, fontSize));
    const { totalWidth, cellWidths } = layout;

    // Create/reuse PIXI.Text objects for each character
    while (this._charTexts.length < chars.length) {
      const t = new PIXI.Text("", charStyle);
      t.anchor.set(0.5);
      this.counterContainer.addChild(t);
      this._charTexts.push(t);
    }
    // Hide excess
    for (let i = chars.length; i < this._charTexts.length; i++) {
      this._charTexts[i].visible = false;
    }

    // Position each character in its fixed-width cell, centered around x=0
    let x = -totalWidth / 2;
    for (let i = 0; i < chars.length; i++) {
      const t = this._charTexts[i];
      t.visible = true;
      t.style = charStyle;
      t.text = chars[i];
      t.x = x + cellWidths[i] / 2;
      t.y = 0;
      t.scale.set(1);
      x += cellWidths[i];
    }

    this.counterContainer.scale.set(1);
  }

  /**
   * Show counter (visibility only)
   */
  showCounter() {
    if (!this.counterContainer) return;
    this.counterContainer.alpha = 1;
  }

  /**
   * Animate counter value from one number to another.
   * Same pattern as WinAnimationSecondaryController.startCounterAnimation.
   */
  startCounterAnimation(fromValue, toValue, duration, onComplete, easing = "easeOutQuad") {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = fromValue;
    this.updateCounterDisplay(fromValue);

    // Start counter sound
    soundManager.play(SOUND_NAME.counter);

    this.counterAnimationRef = anime({
      targets: this,
      currentCounterValue: toValue,
      duration: duration,
      easing: easing,
      update: () => {
        this.updateCounterDisplay(this.currentCounterValue);
      },
      complete: () => {
        this.currentCounterValue = toValue;
        this.updateCounterDisplay(toValue);
        this.counterAnimationRef = null;

        // Stop counter sound and play end sound
        soundManager.stop(SOUND_NAME.counter);
        soundManager.play(SOUND_NAME.counter_end);

        if (onComplete && typeof onComplete === "function") {
          onComplete();
        }
      },
    });
  }

  /**
   * Instantly set counter to specific value (skips animation)
   */
  jumpCounterToValue(value) {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = value;
    this.updateCounterDisplay(value);
    soundManager.stop(SOUND_NAME.counter);
    soundManager.play(SOUND_NAME.counter_end);
  }



  // ===================================================================
  //  MULTIPLIER (same pattern as WinAnimationSecondaryController)
  //  Shows "X57 X BET" below counter — same font, 60% size
  // ===================================================================

  initializeMultiplier() {
    if (!this.outroSpine || !this.outroSpine.skeleton) return;

    const multiplierSlot =
      this.outroSpine.skeleton.findSlot("txt_slot_multiplier");
    if (!multiplierSlot) return;

    this.multiplierContainer = new PIXI.Container();
    this.multiplierContainer.name = "pc_outro_multiplier_attached_to_slot";

    this._multiplierBaseFontSize = Math.round(
      this._counterBaseStyle.fontSize * 0.6,
    );

    this.multiplierText = new PIXI.Text(
      "",
      new PIXI.TextStyle(scaleStyle(this._counterBaseStyle, this._multiplierBaseFontSize)),
    );
    this.multiplierText.anchor.set(0.5);
    this.multiplierText.alpha = 0;
    this.multiplierText.visible = false;

    this.multiplierContainer.addChild(this.multiplierText);

    // Scale text to fit within the bone's width
    const bone = multiplierSlot.bone;
    this._multiplierBoneWidth = bone ? bone.data.length || 400 : 400;

    // Attach to slot
    this.outroSpine.addSlotObject(
      "txt_slot_multiplier",
      this.multiplierContainer,
    );

    // Multiplier stays hidden until counter finishes
  }

  /**
   * Update multiplier display text (e.g., "422x BET")
   */
  updateMultiplierDisplay(winAmount) {
    if (!this.multiplierText || this._betAmount <= 0) return;

    const rawMultiplier = winAmount / this._betAmount;
    const multiplier = parseFloat(rawMultiplier.toFixed(2));
    if (multiplier <= 0) return;

    const betText = this.t("bet").toUpperCase();
    const text = `${multiplier}x ${betText}`;

    // Shrink font size to fit — same pattern as PotCollectionReloadController
    let fontSize = this._multiplierBaseFontSize;
    const maxPixelWidth = this._getScreenLocalMaxWidth(0.85);

    const tempStyle = new PIXI.TextStyle(
      scaleStyle(this._counterBaseStyle, fontSize),
    );
    const metrics = PIXI.TextMetrics.measureText(text, tempStyle);
    if (metrics.width > maxPixelWidth && maxPixelWidth > 0) {
      fontSize = Math.max(
        10,
        Math.floor(fontSize * (maxPixelWidth / metrics.width)),
      );
    }

    this.multiplierText.text = text;
    this.multiplierText.style = new PIXI.TextStyle(
      scaleStyle(this._counterBaseStyle, fontSize),
    );
    this.multiplierText.scale.set(1);
    if (this.multiplierContainer) {
      this.multiplierContainer.scale.set(1);
    }
  }



  // ===================================================================
  //  BUTTON (same pattern as WinAnimationSecondaryController)
  // ===================================================================

  initializeButton() {
    if (!this.outroSpine || !this.outroSpine.skeleton) return;

    const tapSlot =
      this.outroSpine.skeleton.findSlot("txt_slot_tap_to_continue");
    if (!tapSlot) return;

    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name = "pc_outro_continue_attached_to_slot";

    // Same font style as WinAnimationSecondaryController
    const style = FONT_STYLES.introTapToStart;
    const btnFontSize = 42;

    this.buttonText = new PIXI.Text(
      "",
      new PIXI.TextStyle(scaleStyle(style, btnFontSize)),
    );
    this.buttonText.anchor.set(0.5);
    this.buttonText.alpha = 0;
    this.buttonText.visible = false;

    this.buttonContainer.addChild(this.buttonText);

    // Cache bone width for overflow scaling
    const bone = tapSlot.bone;
    this._tapBoneWidth = bone ? bone.data.length || 400 : 400;

    // Attach to slot
    this.outroSpine.addSlotObject(
      "txt_slot_tap_to_continue",
      this.buttonContainer,
    );

    // Make the button clickable — triggers fullscreen + dismissal via component handler.
    // buttonText.visible starts false (see above), so this hit area is naturally
    // inert until the button is actually revealed.
    this.buttonContainer.eventMode = "static";
    this.buttonContainer.cursor = "pointer";
    this.buttonContainer.hitArea = new PIXI.Rectangle(
      -this._tapBoneWidth / 2, -40, this._tapBoneWidth, 80
    );
    this.buttonContainer.on("pointerdown", (evt) => {
      if (evt?.stopPropagation) evt.stopPropagation();
      if (this._buttonClickHandler) {
        this._buttonClickHandler(evt);
      }
    });

    // Button stays hidden until counter finishes
    this.applyButtonOffset();
  }

  /**
   * Push TAP TO CONTINUE down by buttonYOffset × symbolHeight (spine-local).
   * Offset applied to text inside container — spine overwrites container transform.
   */
  applyButtonOffset() {
    if (!this.buttonText) return;
    const config = this.getDeviceMultipliers();
    const yOffset = (config.buttonYOffset || 0) * (this.symbolHeight || 0);
    this.buttonText.y = yOffset;
  }



  // ===================================================================
  //  ANIMATION HELPERS
  // ===================================================================

  getSpineAnimationDuration(animationName) {
    if (!this.outroSpine?.skeleton?.data?.animations) return 1000;
    try {
      const animation = this.outroSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName,
      );
      if (animation && animation.duration) {
        return Math.round(animation.duration * 1000);
      }
      return 1000;
    } catch {
      return 1000;
    }
  }

  clearAllTimeouts() {
    for (const t of this.currentTimeouts) {
      if (t && typeof t.clear === "function") {
        t.clear();
      }
    }
    this.currentTimeouts = [];

    if (this.skipProtectionTimeout) {
      this.skipProtectionTimeout.clear();
      this.skipProtectionTimeout = null;
    }
    if (this.autoCloseTimeout) {
      this.autoCloseTimeout.clear();
      this.autoCloseTimeout = null;
    }
    if (this.debounceTimeout) {
      this.debounceTimeout.clear();
      this.debounceTimeout = null;
    }
    if (this.currentTimeout) {
      this.currentTimeout.clear();
      this.currentTimeout = null;
    }
  }

  // ===================================================================
  //  AUTO-CLOSE (same pattern as WinAnimationSecondaryController)
  //  GDD §19: auto-dismiss after 30 seconds
  // ===================================================================

  startAutoCloseTimer() {
    // Clear any existing auto-close timer
    if (this.autoCloseTimeout) {
      this.autoCloseTimeout.clear();
    }

    this.autoCloseTimeout = new TickerTimeout(() => {
      if (this.isPlaying && !this.hasHandledCloseClick) {
        this.hasHandledCloseClick = true;
        this.playClosingAnimation();
      }
    }, AUTO_CLOSE_DELAY);
  }

  // ===================================================================
  //  ANIMATION SEQUENCE
  // ===================================================================

  /**
   * Set the total win value and bet for display. Called before startSequence.
   * Counter starts at 0 and counts up during the sequence.
   * @param {number} totalWin - The total win amount (raw number)
   * @param {number} bet - The bet amount for multiplier calculation
   */
  setTotalWinValue(totalWin, bet) {
    this._totalWinValue = totalWin || 0;
    this._betAmount = bet || 0;
    // Reset counter to 0 — counting starts in startSequence
    this.currentCounterValue = 0;
    this.updateCounterDisplay(0);
    // Prepare multiplier text (stays hidden until counter finishes)
    this.updateMultiplierDisplay(this._totalWinValue);
    if (this.multiplierText) {
      this.multiplierText.visible = false;
      this.multiplierText.alpha = 0;
    }
    if (this.buttonText) {
      this.buttonText.visible = false;
      this.buttonText.alpha = 0;
    }
  }

  startSequence() {
    return new Promise((resolve) => {
      if (this.isPlaying || !this.outroSpine?.state) {
        resolve();
        return;
      }

      this.isPlaying = true;
      this.isWaitingForClick = false;
      this.hasHandledCloseClick = false;
      this.overlayRef.current?.fadeInBackground();

      this.isHandlingUserClick = false;
      this.canSkipAnimation = false;

      this.clearAllTimeouts();

      // Fade out active feature music (background or ambient — they're exclusive)
      if (soundManager.isAmbientActive) {
        const featureAmbient = HowlCache[SOUND_NAME.feature_ambient_music];
        if (featureAmbient && featureAmbient.playing()) {
          soundManager.fadeOut(SOUND_NAME.feature_ambient_music, 500);
        }
      } else {
        const featureBG = HowlCache[SOUND_NAME.feature_background_music];
        if (featureBG && featureBG.playing()) {
          soundManager.fadeOut(SOUND_NAME.feature_background_music, 500);
        }
      }
      // Remember which was active so we can restore it
      this._wasAmbientActive = soundManager.isAmbientActive;

      // Play total win in sound + fade in loop music
      soundManager.play(SOUND_NAME.total_win_in);
      const totalWinLoop = HowlCache[SOUND_NAME.total_win_loop];
      if (totalWinLoop) {
        totalWinLoop.loop(true);
        soundManager.fadeIn(SOUND_NAME.total_win_loop, 500);
      }

      if (
        this.outroSpine &&
        this.symbolHeight &&
        this.symbolHeight > 0 &&
        this.dimensions
      ) {
        this.applyScaleAndCenter();
      }

      // Clear state
      this.outroSpine.state.clearTracks();
      this.outroSpine.state.clearListeners();
      this.outroSpine.state.apply(this.outroSpine.skeleton);
      this.outroSpine.skeleton.setToSetupPose();

      // Apply skin
      this.applySkin();

      // SKIP PROTECTION: Block skipping for the first second
      this.skipProtectionTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.canSkipAnimation = true;
          this.isWaitingForClick = true;
          // Auto-close timer now starts after counter finishes (in _showMultiplierAfterCounter)
        }
      }, 1000);

      // Play "in" animation while STILL HIDDEN
      this.outroSpine.state.setAnimation(0, "in", false);

      // Show on NEXT FRAME — counter starts immediately with IN animation
      if (this._showRAF) cancelAnimationFrame(this._showRAF);
      this._showRAF = requestAnimationFrame(() => {
        this._showRAF = null;
        if (this.isPlaying && this.outroSpine) {
          this.outroSpine.alpha = 1;

          // Start coin shower — between overlay (zIndex 0) and spine (zIndex 20)
          this._startCoinShower();

          // Show counter and start counting immediately (during IN phase)
          this.showCounter();
          this.startCounterAnimation(0, this._totalWinValue, COUNTER_DURATION, () => {
            if (!this.isPlaying) return;
            // Signal that counter is done — WinDisplay shows "Total Win: X"
            this.dispatch(setPotCollectionOutroCounterFinished(true));
            // Counter finished — fade in multiplier
            this._showMultiplierAfterCounter();
          });
        }
      });

      const inDuration = this.getSpineAnimationDuration("in");

      const inTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.outroSpine.state.setAnimation(0, "idle", true);
          soundManager.play(SOUND_NAME.total_win_idle);
          resolve();
        }
      }, inDuration);

      this.currentTimeout = inTimeout;
    });
  }

  // ===================================================================
  //  USER INTERACTION
  // ===================================================================

  handleUserClick() {
    if (!this.isPlaying || !this.outroSpine?.state) return false;
    if (this.debounceTimeout) return false;
    if (!this.canSkipAnimation) return false;
    if (!this.isWaitingForClick) return false;

    this.debounceTimeout = new TickerTimeout(() => {
      this.debounceTimeout = null;
    }, 300);

    // If counter is still animating, jump to final value first
    if (this.counterAnimationRef) {
      this.jumpCounterToValue(this._totalWinValue);
      this.dispatch(setPotCollectionOutroCounterFinished(true));
      this._showMultiplierAfterCounter();
      return true;
    }

    // Clear auto-close timer since user clicked manually
    if (this.autoCloseTimeout) {
      this.autoCloseTimeout.clear();
      this.autoCloseTimeout = null;
    }

    this.hasHandledCloseClick = true;
    this.playClosingAnimation();
    return true;
  }

  /**
   * Fade in multiplier ("XX X BET") after counter finishes.
   * Starts auto-close timer once multiplier is visible.
   */
  _showMultiplierAfterCounter() {
    if (!this.isPlaying) return;

    const multTimeout = new TickerTimeout(() => {
      if (!this.isPlaying) return;

      if (this.multiplierText && this._betAmount > 0) {
        this.updateMultiplierDisplay(this._totalWinValue);
        this.multiplierText.visible = true;
        this.multiplierText.alpha = 0;
        anime({
          targets: this.multiplierText,
          alpha: 1,
          duration: 400,
          easing: 'easeOutCubic',
        });
      }

      // Reveal "TAP TO CONTINUE" at the same time as multiplier
      if (this.buttonText) {
        this.buttonText.text = this.t("tap_to_continue").toUpperCase();

        // Auto-scale to fit within bone width AND screen width
        if (this.buttonContainer) {
          this.buttonContainer.scale.set(1);
          const spineScale = this.outroSpine?.scale?.x || 1;
          const screenLocal = ((this.dimensions?.width || 800) * 0.8) / spineScale;
          const maxWidth = Math.min(this._tapBoneWidth, screenLocal);
          if (this.buttonText.width > maxWidth && maxWidth > 0) {
            this.buttonContainer.scale.set(maxWidth / this.buttonText.width);
          }
        }

        this.buttonText.visible = true;
        this.buttonText.alpha = 0;
        this.applyButtonOffset();
        anime({
          targets: this.buttonText,
          alpha: 1,
          duration: 400,
          easing: 'easeOutCubic',
        });
      }

      // Start auto-close timer after multiplier is revealed
      this.startAutoCloseTimer();
    }, 200);
    this.currentTimeouts.push(multTimeout);
  }

  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.outroSpine?.state) return;

    this.isWaitingForClick = false;

    this.outroSpine.state.clearListeners();

    // Play "out" animation and its one-shot sound
    this.outroSpine.state.setAnimation(0, "out", false);

    // Crossfade: fade out idle phase sound so it blends with the out sound
    soundManager.fadeOut(SOUND_NAME.total_win_idle, 500);
    soundManager.play(SOUND_NAME.total_win_out);

    // Fade out total win loop over the full "out" animation duration
    // so the music plays through the entire closing sequence
    const outDuration = this.getSpineAnimationDuration("out");
    soundManager.fadeOut(SOUND_NAME.total_win_loop, outDuration);

    this.outroSpine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === "out" && this.isPlaying) {
          // Restore whichever feature music was active before the dialog
          if (this._wasAmbientActive) {
            soundManager.fadeIn(SOUND_NAME.feature_ambient_music, 500);
          } else {
            soundManager.fadeIn(SOUND_NAME.feature_background_music, 500);
          }
          this.finishAnimation();
        }
      },
    });
  }

  finishAnimation() {
    if (!this.outroSpine || !this.initialized) return;

    this.isPlaying = false;
    this.isWaitingForClick = false;

    // Stop counter animation if still running
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }
    soundManager.stop(SOUND_NAME.counter);
    this.outroSpine.alpha = 0.001;

    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }



    // Stop sounds
    soundManager.stop(SOUND_NAME.total_win_loop);

    this.clearAllTimeouts();

    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;

    // CRITICAL: Close the dialog FIRST (synchronous) to prevent re-trigger.
    this.dispatch(setShowPotCollectionOutro(false));

    // Trigger the scene transition (gradient fade → midpoint clears feature → reveal base game)
    this.dispatch(setPotCollectionOutroTransitionActive(true));

    // Fade out the overlay (purely visual — dialog is already closed)
    this.overlayRef.current?.fadeOutBackground();

    // Stop coin shower
    this._stopCoinShower();
  }

  // ===================================================================
  //  COIN SHOWER EFFECT
  // ===================================================================

  _startCoinShower() {
    if (!this.container || !FXManager.isReady) return;

    this._coinShowerContainer = new PIXI.Container();
    this._coinShowerContainer.name = "pc_outro_coin_shower";
    this._coinShowerContainer.zIndex = 10;
    this._coinShowerContainer.y = 0;
    this._coinShowerContainer.eventMode = "none";

    // Scale coin shower to 2× the spine's current scale — always proportional
    const spineScale = this.outroSpine?.scale.x || 1;
    this._coinShowerContainer.scale.set(spineScale * 2.5);

    this.container.addChild(this._coinShowerContainer);

    FXManager.startCoinShower(this._coinShowerContainer);
  }

  _stopCoinShower() {
    FXManager.stopCoinShower();

    if (this._coinShowerContainer) {
      if (this._coinShowerContainer.parent) {
        this._coinShowerContainer.parent.removeChild(this._coinShowerContainer);
      }
      if (!this._coinShowerContainer.destroyed) {
        this._coinShowerContainer.destroy({ children: true });
      }
      this._coinShowerContainer = null;
    }
  }

  // ===================================================================
  //  CLEANUP
  // ===================================================================

  cleanup() {
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    soundManager.stop(SOUND_NAME.total_win_loop);

    // Stop coin shower
    this._stopCoinShower();

    this.clearAllTimeouts();

    if (this.outroSpine?.removeSlotObjects) {
      this.outroSpine.removeSlotObjects();
    }

    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    if (this.counterContainer) {
      this.counterContainer.destroy({ children: true });
      this.counterContainer = null;
      this._charTexts = [];
    }

    if (this.multiplierContainer) {
      this.multiplierContainer.destroy({ children: true });
      this.multiplierContainer = null;
      this.multiplierText = null;
    }

    if (this.buttonContainer) {
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
      this.buttonText = null;
    }

    if (this.outroSpine) {
      this.outroSpine.state?.clearListeners();
      this.outroSpine.state?.clearTracks();
      if (this.outroSpine.state) this.outroSpine.state.timeScale = 1.0;

      if (this.container?.children.includes(this.outroSpine)) {
        this.container.removeChild(this.outroSpine);
      }
      this.outroSpine.destroy();
      this.outroSpine = null;
    }

    this.container = null;
    this.initialized = false;
  }
}

export default PotCollectionOutroDialogController;
